import { useContext } from "react";
import { z } from "zod";

import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { useParsedQuery } from "@nens/react-query-utils";
import { removeNullUndefined } from "@nens/shared-utils";

import { API_BASE_URL, AuthMode } from "../constants";
import { FilterSet } from "../domain/filter-set";

export interface FacetOption {
  option: string | number;
  count: number;
}

const LIMIT = 100;

export function useFacets(mode: AuthMode, facet: string, filters: FilterSet) {
  const { apiFetchFunction, tenantId } = useContext(LdoAuthContext);
  const url = `${API_BASE_URL}/scenarios/facet/?mode=${mode}&facet=${facet}&limit=${LIMIT}`;

  // To get options for checks for a given facet, we need to exclude the options
  // that have already been chosen for it.
  const urlFilters = filters.removeAll(facet).toUrlParams();

  const urlWithFilters = urlFilters ? `${url}&${urlFilters}` : url;

  const schema = z.array(
    z.object({
      [facet]: z.string().or(z.number()).nullable(),
      count: z.number(),
    })
  );

  return useParsedQuery<FacetOption[]>(
    [tenantId, "ldo-api-v1", "facets", urlWithFilters],
    async () => {
      const allResults: unknown[] = [];
      let page = 0;
      let keepFetching = true;

      while (keepFetching) {
        const offset = page * LIMIT;
        const batch = (await apiFetchFunction(`${urlWithFilters}&offset=${offset}`)) as unknown[];
        allResults.push(...batch);

        if (batch.length < LIMIT) {
          keepFetching = false;
          break;
        }
        page++;
      }

      return allResults;
    },
    (json: unknown) => {
      const parsed = schema.parse(json);
      const result = removeNullUndefined(
        parsed.map((parsedField) =>
          parsedField[facet] !== null
            ? ({
                option: parsedField[facet],
                count: parsedField.count,
              } as FacetOption)
            : null
        )
      );
      // Sort alphabetically, so order is consistent
      result.sort((a, b) => (a.option < b.option ? -1 : a.option > b.option ? 1 : 0));
      return result;
    }
  );
}
